import * as autobind from 'autobind';
import * as React from 'react';
import { WithTranslation, withTranslation } from 'react-i18next';
import { connect } from 'redux-scaffolding-ts';
import { getOptionsFromValue, QueryResult } from '../../stores/dataStore';
import { SelectionInput, SelectionItem } from '../form/selectionInput';
import { nameof } from '../../utils/object';
import { ItemReference } from '../../stores/dataStore';
import { isNullOrWhiteSpaces, customEqualCompare } from '../../utils/useful-functions';
import { VisaDto, VisaStore } from '../../stores/configuration/planner-assistant/visas-store';

export interface VisaItemReference extends ItemReference {
  visaTreatyRequired: boolean;
}

interface VisaEditorProps extends WithTranslation {
  value: VisaItemReference;
  onChange?: (value: VisaItemReference) => void;
  placeholder?: string;
  visas?: VisaStore;
  nullable?: boolean;
  minWidth?: number;
  reloadOnChange?: boolean;
  clearOnReload?: boolean;
  clearable?: boolean;
  readOnly?: boolean;
  className?: string;
  loadDataOnOpen?: boolean;
}

interface VisaEditorState {
  value: VisaItemReference;
  searchQuery: string;
  query?: (searchQuery: string) => Promise<QueryResult<ItemReference>>;
  options: SelectionItem[];
  currentQuery?: QueryResult<VisaDto>;
  isAlreadyQuerying: boolean;
}

@connect(['visas', VisaStore])
class VisaEditor extends React.Component<VisaEditorProps, VisaEditorState> {
  private get visasStore() {
    return this.props.visas;
  }

  constructor(props: Readonly<VisaEditorProps>) {
    super(props);
    this.state = {
      value: this.props.value,
      searchQuery: null,
      query: !this.props.loadDataOnOpen ? null : this.getVisaMethod(),
      options: getOptionsFromValue(this.state?.query, this.props.value),
      currentQuery: null,
      isAlreadyQuerying: false
    };
  }

  componentDidUpdate(prevProps: Readonly<VisaEditorProps>) {
    const { value, reloadOnChange } = this.props;

    const prevPropsValue = prevProps.value ? (prevProps.value as VisaItemReference).id : null;
    const propsValue = value ? (value as VisaItemReference).id : null;
    const equalCompare = customEqualCompare(prevPropsValue, propsValue);

    if (reloadOnChange && !equalCompare) {
      this.setState({
        value: value,
        searchQuery: null,
        options: getOptionsFromValue(this.state?.query, value)
      });
    }
  }

  onOpenDropDown = () => {
    if (this.props.loadDataOnOpen && !this.state.query) {
      this.setState({ query: this.getVisaMethod() });
    }
  };

  @autobind
  private getVisaMethod() {
    const method = async (search: string) => {
      if (this.state.isAlreadyQuerying) return;
      this.setState({ isAlreadyQuerying: true });
      const filters = isNullOrWhiteSpaces(search) ? [] : [`contains(tolower(${nameof<VisaDto>('name')}), '${search.toLowerCase()}')`];

      const result = await this.visasStore.getAllAsync({
        searchQuery: '',
        skip: 0,
        take: 100000,
        orderBy: [{ direction: 'Ascending', field: nameof<VisaDto>('name'), useProfile: false }],
        filter: filters
      });

      this.setState({ isAlreadyQuerying: false });

      return Object.assign({}, result, {
        items: result.items.map(c => ({ id: c.id, title: c.name, visaTreatyRequired: c.internationalTreatyRequired }))
      }) as QueryResult<VisaItemReference>;
    };
    return method;
  }

  public render() {
    const val = this.state.value;
    const query = this.state.query;
    const options = this.state.options;

    return (
      <SelectionInput
        content={item => <div>{item.title}</div>}
        searchable
        className={this.props.className}
        readOnly={this.props.readOnly}
        minWidth={this.props.minWidth}
        nullable={this.props.nullable}
        placeholder={this.props.placeholder}
        query={query}
        value={val}
        searchQuery={this.state.searchQuery}
        onQueryChange={q => this.setState({ searchQuery: q })}
        onChange={value => this.props.onChange(value as any)}
        clearable={this.props.clearable}
        clearOnReload={this.props.clearOnReload}
        onOpenDropDown={this.onOpenDropDown}
        options={query != null ? null : options}
      />
    );
  }
}

// Wire up the React component to the Redux store
export default withTranslation()(VisaEditor);
